import React, {Fragment, useMemo, useRef, useState} from 'react';
import {Combobox, Transition} from "@headlessui/react";
import {ArrowTurnDownRightIcon, CheckIcon, ChevronUpDownIcon} from "@heroicons/react/24/solid";
import {classNames} from "../../../utils/common";

function buildParentPathById(categories) {
    const parentPathById = new Map();
    const stack = [];

    categories.forEach((categoryOption) => {
        while (stack.length && stack[stack.length - 1].level >= categoryOption.level) {
            stack.pop();
        }
        parentPathById.set(categoryOption.id, stack.map((ancestor) => ancestor.name).join(' › '));
        stack.push(categoryOption);
    });

    return parentPathById;
}

const CategoryPicker = ({label, category, setCategory, categories, smallerSize = false, ...props}) => {
    const [query, setQuery] = useState('');
    const inputRef = useRef(null);
    const isFiltering = query.trim().length > 0;

    function selectInputText(input = inputRef.current) {
        if (!input) {
            return;
        }
        // Defer so selection survives focus/open handling and mouseup caret placement.
        requestAnimationFrame(() => input.select());
    }

    const selectorCategoryWidth = useMemo(() => {
        let maxWidth = 1;
        categories.forEach(category => {
            let categoryWidth = category.name.length * 8 + category.level * 20;
            if (categoryWidth > maxWidth) {
                maxWidth = categoryWidth;
            }
        });
        return maxWidth + (smallerSize ? 60 : 100) + 'px';
    }, [categories, smallerSize]);

    const parentPathById = useMemo(() => buildParentPathById(categories), [categories]);

    const filteredCategories = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        if (!normalizedQuery) {
            return categories;
        }
        return categories.filter(categoryOption =>
            categoryOption.name.toLowerCase().includes(normalizedQuery)
        );
    }, [categories, query]);

    function handleChange(nextCategory) {
        setCategory(nextCategory);
        setQuery('');
    }

    function renderCategoryLabel(categoryOption, {selected, active}) {
        const parentPath = parentPathById.get(categoryOption.id);

        if (isFiltering) {
            return (
                <span className={classNames(
                    selected ? 'font-semibold' : 'font-normal',
                    smallerSize ? 'ml-1' : 'ml-3',
                    'block truncate'
                )}>
                    {parentPath && (
                        <span className={active ? 'text-indigo-200' : 'text-gray-400'}>
                            {parentPath} ›{' '}
                        </span>
                    )}
                    {categoryOption.name}
                </span>
            );
        }

        return (
            <span
                className={classNames(
                    selected ? 'font-semibold' : 'font-normal',
                    smallerSize ? 'ml-1' : 'ml-3',
                    'flex items-center truncate'
                )}
                style={{paddingLeft: `${Math.max(categoryOption.level - 1, 0) * (smallerSize ? 12 : 16)}px`}}
            >
                {categoryOption.level > 1 && (
                    <ArrowTurnDownRightIcon
                        className={classNames(
                            smallerSize ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-1.5',
                            active ? 'text-indigo-200' : 'text-gray-400',
                            'flex-shrink-0'
                        )}
                        aria-hidden="true"
                    />
                )}
                {categoryOption.name}
            </span>
        );
    }

    return (
        <Combobox as="div" value={category} by="id" onChange={handleChange} {...props}>
            {label && <Combobox.Label className={classNames(
                smallerSize ? "ml-1 text-xs" : "ml-3 text-sm",
                "block font-medium text-gray-700"
            )}> {label}</Combobox.Label>}
            <div className={classNames(smallerSize ? "mt-0.5 h-7" : "mt-1 h-10", "relative")}>
                <Combobox.Input
                    ref={inputRef}
                    className={classNames(
                        smallerSize
                            ? "h-7 pl-1.5 pr-7 py-1 text-xs"
                            : "h-10 pl-3 pr-10 py-2 sm:text-sm",
                        "w-full bg-white border border-gray-300 rounded-md shadow-sm text-left focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    )}
                    style={{minWidth: smallerSize ? Math.min(parseInt(selectorCategoryWidth, 10) || 160, 180) + 'px' : 250}}
                    displayValue={(selectedCategory) => selectedCategory?.name ?? ''}
                    onChange={(event) => setQuery(event.target.value)}
                    onFocus={(event) => selectInputText(event.target)}
                    onClick={(event) => selectInputText(event.target)}
                />
                <Combobox.Button
                    className={classNames(
                        smallerSize ? "pr-1.5" : "pr-3",
                        "absolute inset-y-0 right-0 flex items-center focus:outline-none"
                    )}
                    onClick={() => selectInputText()}
                >
                    <ChevronUpDownIcon className={classNames(smallerSize ? "h-4 w-4" : "h-5 w-5", "text-gray-400")} aria-hidden="true"/>
                </Combobox.Button>

                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery('')}
                >
                    <Combobox.Options className={classNames(
                        smallerSize ? "text-xs" : "text-base sm:text-sm",
                        "absolute z-10 mt-1 w-full bg-white shadow-lg max-h-96 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none"
                    )}>
                        {filteredCategories.length === 0 && (
                            <div className={classNames(
                                smallerSize ? "py-1 px-2" : "py-2 px-3",
                                "text-gray-500 cursor-default select-none"
                            )}>
                                No categories found
                            </div>
                        )}
                        {filteredCategories.map((categoryOption) => (
                            <Combobox.Option
                                key={categoryOption.id}
                                className={({ active }) =>
                                    classNames(active
                                            ? 'bg-indigo-600 text-white'
                                            : 'text-gray-900',
                                        smallerSize ? 'py-1 pl-2 pr-7' : 'py-2 pl-3 pr-9',
                                        'relative cursor-default select-none')
                                }
                                value={categoryOption}
                            >
                                {({ selected, active }) => (
                                    <>
                                        <div className="flex items-center">
                                            {renderCategoryLabel(categoryOption, {selected, active})}
                                        </div>
                                        {selected && <span className={classNames(
                                            active ? 'text-white' : 'text-indigo-600',
                                            smallerSize ? 'pr-2' : 'pr-4',
                                            'absolute inset-y-0 right-0 flex items-center'
                                        )}>
                                            <CheckIcon className={classNames(smallerSize ? "h-4 w-4" : "h-5 w-5")} aria-hidden="true"/>
                                        </span>}
                                    </>
                                )}
                            </Combobox.Option>
                        ))}
                    </Combobox.Options>
                </Transition>
            </div>
        </Combobox>
    );
};

export default CategoryPicker;
